import { useIsActorSyncPreparing } from "@-/dev.improve.haniverse.routes.nav._context";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import {
  useLearningStatsCatalogQueryOptions,
  useLearningStatsCoverageHistoryQueryOptions,
  useLearningStatsSuspense,
  useLearningStatsReviewHistoryQueryOptions,
} from "@-/dev.improve.haniverse.lib.learning-stats";
import { useRefetchOnTabFocus } from "@-/dev.improve.haniverse.routes.nav._lib.tab-focus-refetch";
import { ScreenReadyMarker } from "@-/dev.improve.haniverse.components.ui.screen-ready-marker";
import type { ReactNode } from "react";

/**
 * First paint, focus refresh and the sync fallback for every Stats sub-page. Publishes nothing:
 * each section subscribes to the slice it renders (`useLearningStatsSuspense` for the prefetched
 * core, the progressive catalog / history hooks for the rest).
 */
export function LearningStatsPageSuspense({
  children,
  syncFallback,
}: {
  children: ReactNode;
  syncFallback?: ReactNode;
}) {
  const isSyncing = useIsActorSyncPreparing();
  const queryClient = useQueryClient();
  const catalogQueryOptions = useLearningStatsCatalogQueryOptions();
  const reviewHistoryQueryOptions = useLearningStatsReviewHistoryQueryOptions();
  const coverageHistoryQueryOptions = useLearningStatsCoverageHistoryQueryOptions();
  // Stays a suspense query — and is ACTOR-DB ONLY, so the first paint never waits on the
  // static catalog: the FIRST mount suspends into the page's `SuspensePageBoundary` (the on-tap
  // navigator prefetch usually warms it first, so it resolves immediately), which keeps the
  // "prefetch-past-suspense" contract intact. Hidden route bodies retire their observers and
  // remount from the cache on return. The focus refetch also refreshes a body kept alive by an
  // open sheet; Suspense keeps its previous data visible during that background refetch.
  // The catalog (HSK levels, corpus frequency — served online on native) and both histories stay
  // progressive: the sections that show them subscribe themselves and render their own loading
  // state, so neither holds the page behind the Suspense fallback.
  const { data: coreData, refetch: refetchCore } = useLearningStatsSuspense();
  useRefetchOnTabFocus((options) =>
    Promise.all([
      refetchCore(options),
      // Refreshes the catalog only where a section observes it (or it is cached); the Overall
      // tab reads none of it.
      queryClient.refetchQueries({ exact: true, queryKey: catalogQueryOptions.queryKey }, options),
      // Core includes the time-sensitive due count, but history changes only after writes.
      // fetchQuery checks the transitive write clock and shares an in-flight read; forcing
      // observer.refetch here rescanned both histories on every otherwise quiet tab return.
      queryClient.fetchQuery(reviewHistoryQueryOptions),
      queryClient.fetchQuery(coverageHistoryQueryOptions),
    ]).catch(() => {
      // fetchQuery rejects on failure; the sections' own query observers own the progressive
      // history error UI. A focus refresh must not also create an unhandled rejection.
    }),
  );

  if (
    syncFallback !== undefined &&
    isSyncing &&
    Object.values(coreData.cardsByStage).every((count) => count === 0)
  ) {
    return syncFallback;
  }

  return (
    <>
      {children}
      {/* Screen-render timing end mark: this component only renders once the suspense query above
          has resolved, so mounting here marks "navigated → stats data on screen" (`tti`). Each
          stats sub-route mounts its own instance, so the mark fires on each sub-page's FIRST
          open (the route pattern in the metric tells them apart). */}
      <ScreenReadyMarker screen="stats" />
    </>
  );
}
